import { useEffect, useState } from "react";
import { Button, CardHeader, ValidationMessage } from "components";
import { UserCognito, Step } from "helpers";
import { updateMFAPreference } from "aws-amplify/auth";
import { useAuthContext } from "context";
import { VerifyPhoneNumberForm } from "features";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

interface AuthVerifyPhoneNumberProps {
  user: UserCognito;
  variant?: "auth" | "profile";
}

export const AuthVerifyPhoneNumber = ({ user, variant = "auth" }: AuthVerifyPhoneNumberProps) => {
  const { setAuth } = useAuthContext();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(0);
  const [error, setError] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);
  const [title, setTitle] = useState("");
  const [paragraph, setParagraph] = useState("");

  const onSet2FA = async () => {
    try {
      setDisabled(true);
      // Set the preferred MFA to SMS_MFA
      await updateMFAPreference({ sms: "ENABLED" });
      setAuth("success");
      // Redirect to root after successful MFA setup
      navigate("/");
    } catch (err: any) {
      console.error("Set 2FA error:", err);
      const errorMessages: Record<string, string> = {
        InvalidParameterException: t("auth.invalidMFAConfiguration"),
        NotAuthorizedException: t("auth.notAuthorizedToSetMFA"),
      };
      setError(errorMessages[err.name] || err.message || t("auth.anErrorOccuredWhileSetting2FA"));
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (step === 0) {
      setTitle(t("auth.pleaseVerifyPhoneNumber"));
      setParagraph(t("auth.youWillNoLongerSeeThisWindow"));
    } else {
      setTitle(t("auth.yourPhoneNumberHasBeenVerified"));
      setParagraph("");
    }
  }, [step]);

  const set2FALater = (
    <span className="cursor-pointer text-xs leading-6 hover:underline" onClick={() => setAuth("success")}>
      {t("auth.set2FALater")}
    </span>
  );

  return (
    <>
      {step === 0 ? (
        <div className="flex flex-col gap-2">
          <CardHeader title={title} paragraph={paragraph} />
          <VerifyPhoneNumberForm onContinue={() => setStep(1)} user={user} variant={variant} cancel={set2FALater} />
        </div>
      ) : (
        <div className="flex flex-col gap-20">
          <div className="flex-1"></div>
          <div className="px-20">
            <CardHeader withLanguage={false} title={title} paragraph={paragraph} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-1">
              <div className="flex flex-1 items-center justify-center">{set2FALater}</div>
              <div className="flex-1">
                <Button disabled={disabled} onClick={onSet2FA}>
                  {t("auth.set2FA")}
                </Button>
              </div>
            </div>
            <ValidationMessage>{error}</ValidationMessage>
          </div>
        </div>
      )}
    </>
  );
};
